<template>
  <section class="data-table-wrapper" :class="[datasetName]">
    <slot name="filter">
      <div v-if="!hideFilter" class="table-filter-expression-wrapper">
        <filter-expression
            v-if="filterColumns.length > 0"
            class="header-filter-expression"
            :columns="filterColumns"
            :dataset-name="datasetName"
            :filter-groups="filterGroups"
            :operator="operator"
            @filters-updated="updateFilterGroups"
            @operator-changed="updateOperator"
        />
      </div>
    </slot>
    <vy-table
        :columns="columns"
        :categories="categories"
        :sources="sources"
        :data-index="dataIndex"
        :table-data="localTableData"
        :filter="filter"
        :persistence-I-D="persistenceId"
        :auto-fetch-config="autoFetchConfig"
        :reload-data="reloadData"
        :set-first-page="setFirstPage"
        :shown-columns="shownColumns"
        :show-edit="!hideEdit"
        :disable-row-selection="disableRowSelection"
        :nested-field-separator="nestedFieldSeparator"
        @data-loaded="handleDataLoaded"
        @data-changed="handleDataChanged"
        @data-filtered="handleDataFiltered"
        @column-selection="handleColumnSelectionChange"
        @table-sorted="handleSortChange"
        @row-click="handleRowClick"
        @row-expanded="handleRowExpanded"
        @table-persistence-updated="handlePersistenceUpdated"
    />
  </section>
</template>
<script>
import _ from 'lodash';
import VyTable from '../../table/src/table.vue';
import FilterExpressionService from '../../../modules/assurance/services/filter.expression.service';
import FilterExpression from '../../filterExpression/src/filterExpression.vue';
import FilterModel from '../../../modules/assurance/components/filter/data/filter.model.js'
import FormatterCommon from '../../../app/data-formatters/formatter-common';
import MetadataService from "../../../services/metadata.service";
import PreferenceService from "../../../services/preference.service";
import UtilsService from '../../../services/utils.service';
import VueDatasetService from "../../../services/vue-dataset-view.service";
import {mapGetters} from "vuex";
import {store} from "../../../modules/admin/store";
import FilterMigrationService from "../../../services/filter.migration.service";

const REFRESH_INTERVAL = 30 * 1000; // 30 seconds
const SHOW_DATA_SOURCES_PANEL_MAP = {
  'systems': true,
  'calls': true,
};

export default {
  name: 'DataSelectionTable',
  props: {
    // from knockout params
    appId: { type: String, required: true },
    viewId: { type: String, required: true },
    datasetName: { type: String, default: 'rows' }, // This should be a PLURAL noun to describe the data, e.g. "Endpoints", "Calls", etc.
    filterModel: { type: Object, default: () => new FilterModel() },
    dateRange: { type: Object },
    isLiveMode: { type: Boolean, default: false },
    hideFilter: { type: Boolean, default: false },
    hideEdit: { type: Boolean, default: false },
    disableRowSelection: { type: Boolean, default: true },
    nestedFieldSeparator: { type: String },
    filter: { type: Array },
    localDataMode: { type: Boolean, default: false },
    localSort: { type: Boolean, default: false },
    noPagination: { type: Boolean, default: false },
    localTableData: { type: Array, default: () => [] },
    rowClickCallback: { type: Function },
    dataChangedCallback: { type: Function },
    dataLoadedCallback: { type: Function },
    dataFilteredCallback: { type: Function },
    autoReloadCallback: { type: Function },
    watchOrgContext: { type: Boolean, default: false },
  },
  data: function() {
    return {
      VIEW_ID: this.viewId,
      autoFetchConfig: this.localDataMode ? undefined : {
        // this url isn't actually used since we're overriding tabulator's ajax functionality, but it's required to trigger the data load
        url: this.VIEW_ID,
        localSort: this.localSort,
        noPagination: this.noPagination,
        ajaxRequestFunc: (url, config, tableParams) => {
          self = this;
          return new Promise((resolve, reject) => {
            if (self.loadingConfigComplete) { 
              // copy to new object to avoid mutating original params
              let requestParams = Object.assign({}, tableParams);
              if (self.dateRange) {
                // default to live data
                let startDate = moment().subtract(1, 'hour');
                let endDate = moment();

                if (self.dateRange.startDate) {
                  startDate = self.dateRange.startDate;
                }
                if (self.dateRange.endDate) {
                  endDate = self.dateRange.endDate;
                }

                requestParams.start = UtilsService.convertMomentToUtcAndFormatServerReady(startDate);
                requestParams.end = UtilsService.convertMomentToUtcAndFormatServerReady(endDate);
              }

              // convert from 1-index to 0-index pagination
              if (requestParams.page) {
                requestParams.page = requestParams.page - 1;
              } else {
                requestParams.page = 0;
              }


              if (requestParams.sorters && requestParams.sorters.length) {
                requestParams.sort = VueDatasetService.getSortParam(requestParams.sorters);
                requestParams.dataSort = VueDatasetService.getDataSortParam(requestParams.sorters);
                delete requestParams.sorters;
              }

              if (self.filterModel) {
                let filterString = self.filterModel.getActiveFiltersString();
                if (filterString) {
                  requestParams.filter = filterString;
                }
                if (self.filterModel.activeFilterExpression) {
                  requestParams.dataFilter = FilterMigrationService.migrateFilterExpressionToApiFilter(self.filterModel.getActiveFiltersForQuery(), self.datasetName);
                }
              }

              VueDatasetService.getDataset(self.datasetName, requestParams).then(
                  (responseData) => {
                    if (!responseData) {
                      reject();
                      return;
                    }
                    let safeData = [];
                    if (responseData.content && responseData.content.length > 0) {
                      safeData = responseData.content.map(function (row) {
                        return FormatterCommon.sanitize(row);
                      });
                    }
                    resolve(self.noPagination ? safeData :
                        {
                          last_page: responseData.totalPages || 0,
                          data: safeData
                        });
                  });

              // TODO: Error handling
            }
          });
        }
      },
      activeFilters: undefined,
      categories: undefined,
      columns: [],
      dataIndex: undefined,
      datasetMetadata: undefined,
      filterColumns: [],
      filterGroups: undefined,
      loadingConfigComplete: false,
      operator: undefined,
      persistenceId: undefined,
      reloadData: 1,
      setFirstPage: 1,
      shownColumns: [],
      sources: undefined,
      tableSorters: undefined,
      timeoutId: undefined,
      watchOrgContextAndReload: this.watchOrgContext,
    }
  },
  computed: {
    ...(store && store.getters['user/getOrgContext']) ? mapGetters('user', {
      orgContext: 'getOrgContext'
    }) : {}
  },
  watch: {
    watchOrgContext: function() {
      this.watchOrgContextAndReload = this.watchOrgContext;
    },
    orgContext: async function() {
      if (this.watchOrgContextAndReload) this.reloadDataFunc();
    },
    datasetName: function() {
      this.loadMetadata();
    },
    dateRange: function(newRange, oldVal) {
      if (!newRange.startDate.isSame(oldVal.startDate) || !newRange.endDate.isSame(oldVal.endDate)) {
        this.reloadDataFunc();
      }
    },
    filterModel: function() {
      let self = this;
      this.updateFilterExpression(this.filterModel.getActiveFilters());
      this.filterModel.activeFiltersUpdated.add(function () {
        self.updateFilterExpression(self.filterModel.getActiveFilters());
      })
    },
    isLiveMode: function() {
      if (this.isLiveMode) { // when live mode is toggled on, reestablish the automatic update
        clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(() => {
          this.reloadDataFunc(true);
        }, REFRESH_INTERVAL);
      }
    }
  },
  components: {
    VyTable,
    FilterExpression
  },
  methods: {
    updateFilterExpression: function(filterExpression) {
      if (filterExpression) {
        this.filterGroups = filterExpression.filterGroups;
        this.operator = filterExpression.operator;

        this.reloadDataFunc();
      }
    },
    updateFilterGroups: function(filterGroups) {
      this.activeFilters.filterGroups = filterGroups;
      this.filterModel.setActiveFilters(this.activeFilters);

    },
    updateOperator: function(newOperator) {
      this.activeFilters.operator = newOperator;
      this.filterModel.setActiveFilters(this.activeFilters);
    },
    handleColumnSelectionChange: function (selectedColumnIds) {
      if (this.localDataMode) {
        return;
      }
      // TODO: do we want to save column prefs for participants table?
      // todo prefs for participants
      let allColumns = this.columns.map(col => col.field);
      let hiddenColumns = _.difference(allColumns, selectedColumnIds);
      PreferenceService.saveDatasetViewPreferences(this.appId, this.viewId,
          {gridConfig: {hiddenColumns}});
    },
    handleSortChange: function (sorters) {
      let sortModel = sorters.map(sorter => {
        return {
          colId: sorter.field,
          sort: sorter.dir
        }
      });
      if (!_.isEqual(this.tableSorters, sortModel)) {
        this.tableSorters = sortModel;
        PreferenceService.saveDatasetViewPreferences(this.appId, this.viewId,
            {gridConfig: {sortModel: sortModel}});
      }
    },
    handleRowClick: function (event, row) {
      const self = this;
      if (self.rowClickCallback) {
        self.rowClickCallback(row, event, reloadData);
        function reloadData() {
          self.reloadDataFunc();
        }
      }
    },
    handleDataLoaded: function (data, visibleData, tabulatorRows) {
      if (this.dataLoadedCallback) {
        this.dataLoadedCallback(data, visibleData, tabulatorRows);
      }
    },
    handleDataChanged: function (data, visibleData) {
      if (this.dataChangedCallback) {
        this.dataChangedCallback(data, visibleData);
      }
    },
    handleDataFiltered: function (data, visibleData) {
      if (this.dataFilteredCallback) {
        this.dataFilteredCallback(data, visibleData);
      }
    },
    handleRowExpanded: function (row, subTable) {
      let data = row.getData();
      if (data && data.meeting_id && data.start_time) {
        VueDatasetService.getParticipantsDataset(data.meeting_id, data.start_time).then(
            result => {
              subTable.setData(result).then(() => {
                subTable.redraw();
              });
            });
      }
    },
    handlePersistenceUpdated: function (id, type, data) {
      if (type === 'columns') {
        PreferenceService.saveDatasetViewPreferences(this.appId, this.viewId, { tabulatorColumns: data });
      }
    },
    loadMetadata: function () {
      let self = this;
      self.loadingConfigComplete = false;
      let datasetName = self.datasetName;
      MetadataService.fetchDatasetMetadata(datasetName).then(datasetMetadata => {
        // setup filter model metadata
        self.datasetMetadata = datasetMetadata;

        self.filterModel.setDatasetMetadata(self.datasetMetadata); // this is also being done in dataset-view.vm, but if the filterModel is not passed in, it needs to be done

        MetadataService.fetchDataSourcesMetadata().then(dataSourcesMetadata => {
          PreferenceService.getDatasetViewPreferences(self.appId, self.viewId, datasetMetadata).then(preferences => {
            self.columns = VueDatasetService.convertColumns(datasetMetadata.columns, undefined, undefined, self);
            self.categories = datasetMetadata.categories;
            if (SHOW_DATA_SOURCES_PANEL_MAP[self.datasetName]) {
              self.sources = VueDatasetService.getDataSourcesConfig(datasetName, datasetMetadata, dataSourcesMetadata);
            } else {
              self.sources = undefined;
            }
            self.persistenceId = `vyopta_cpmm_${self.VIEW_ID}`; // e.g. 'vyopta_cpmm_dataset_calls' -- the table component will prepend 'tabulator-' to this id,
            // so the localStorage key for this example would be 'tabulator-vyopta_cpmm_dataset_calls-[type]',
            // where 'type' is the tabulator persistence type ("sort", "filter", "group", "page" or "columns")

            if (preferences) {
              var gridConfig = preferences.gridConfig || {};
              let prefColumnRevision = 0;
              let newPrefColumnRevision = 0;
              if (!_.isEmpty(gridConfig) && gridConfig['columnRevision']) {
                prefColumnRevision = gridConfig['columnRevision'];
              }

              _.forEach(self.datasetMetadata.columns, col => {
                if (col.revision && col.revision > prefColumnRevision) {
                  if (_.isEmpty(gridConfig) || !gridConfig.hiddenColumns) {
                    gridConfig.hiddenColumns = [];
                  }
                  if (col['notShown']) {
                    if (!_.includes(gridConfig.hiddenColumns, col.id)) {
                      gridConfig.hiddenColumns.push(col.id);
                    }
                  } else {
                    if (_.includes(gridConfig.hiddenColumns, col.id)) {
                      gridConfig.hiddenColumns = gridConfig.hiddenColumns.filter(c => c !== col.id);
                    }
                  }
                  newPrefColumnRevision = Math.max(newPrefColumnRevision, col.revision);
                }
              });
              let columnIds = self.columns.map(col => col.field);
              let sortModel = (preferences && preferences.gridConfig) ? preferences.gridConfig.sortModel : null;
              if (sortModel && sortModel.length) {
                self.columns = VueDatasetService.addSortingToColumns(self.columns, sortModel);
              }
              self.shownColumns = _.difference(columnIds, gridConfig.hiddenColumns);

              if (newPrefColumnRevision) {
                PreferenceService.saveDatasetViewPreferences(self.appId, self.viewId, { gridConfig: { hiddenColumns: gridConfig.hiddenColumns, columnRevision: newPrefColumnRevision } });
              }

              if (preferences.tabulatorColumns) {
                let columnOrder = preferences.tabulatorColumns.map(column => column.field);
                let originalColumnOrder = self.columns.map(column => column.field);
                self.columns.sort(function (left, right) {
                  let a = columnOrder.indexOf(left.field);
                  let b = columnOrder.indexOf(right.field);
                  if (a === -1 && b === -1) {
                    return originalColumnOrder.indexOf(left.field) - originalColumnOrder.indexOf(right.field);
                  } else if (a === -1) {
                    return 1;
                  } else if (b === -1) {
                    return -1;
                  }
                  return a - b;
                });
                
                if (datasetName === 'calls') { // hack to move Overall quality before latest quality
                  let qIndex = self.columns.findIndex((item) => item.field === 'quality');
                  let aIndex = self.columns.findIndex((item) => item.field === 'avg_quality');
                  if (aIndex > qIndex && qIndex >= 0 && aIndex > 0) {
                    const [movedElement] = self.columns.splice(aIndex, 1);
                    self.columns.splice(qIndex, 0, movedElement);
                  }
                }
              }
            }

            self.dataIndex = datasetMetadata.indexColumn;

            // setting available filters needs to happen in the setTimeout
            let availableFilters = _.get(self.datasetMetadata, 'columns');
            if (!_.isNil(availableFilters)) {
              // we need to update self.columns but using the same array object so the viewFilter will pick up the changes
              if (self.filterColumns.length > 0) {
                self.filterColumns.length = 0;
              }
              let filteredColumns = FilterExpressionService.getColumnsFromFilters(availableFilters.filter(column => column.datasetId || (!column.invisible && !column.suppressFiltering))).filter(column => column.filterType !== 'none');
              self.filterColumns.push(...filteredColumns);
            }

            // filter initialization
            if (self.filterModel && !self.filterModel.isEmpty()) {
              self.activeFilters = self.filterModel.getActiveFilters()
            } else {
              self.activeFilters = { filterGroups: VueDatasetService.getQuickStartFilterGroups(self.datasetName, self.datasetMetadata), operator: 'and' };
            }
            self.filterGroups = self.activeFilters.filterGroups;
            // if filters exist, trigger initial filter
            // if (self.filterModel.getActiveFiltersString() || self.hideFilter) {
            //   self.reloadDataFunc(); // todo this might not be right - seeing case where loading screen not showing because this is being called here
            // }

            // if (!self.localDataMode && !self.timeoutId) {
            //   self.timeoutId = setTimeout(() => {
            //     self.reloadDataFunc(true);
            //   }, REFRESH_INTERVAL);
            // }

            self.filterModel.activeFiltersUpdated.add(function () {
              self.updateFilterExpression(self.filterModel.getActiveFilters());
            });
            self.$nextTick(() => { // wait till after all the vue data reactive updates happen before completing and forceing a single data load
              self.loadingConfigComplete = true;
              self.reloadDataFunc();
            });
          });
        });
      });
    },
    reloadDataFunc: function(isAutoReload = false) {
      clearTimeout(this.timeoutId); // clear existing timeout

      // only reload data for remote data tables, or when the auto reload is fired and live mode is ON
      if (this.localDataMode
        || (!this.isLiveMode && isAutoReload)) {
        return;
      }

      if (this.loadingConfigComplete) {
        this.reloadData++; // trigger a reload of table data
        if (this.isLiveMode) {
          this.timeoutId = setTimeout(() => {
            this.reloadDataFunc(true);
          }, REFRESH_INTERVAL); // reload every 30 seconds
        }

        if (isAutoReload && this.autoReloadCallback) {
          this.autoReloadCallback();
        }
      }
    },
  },
  mounted: function() {
    this.loadMetadata();
  },
  beforeDestroy: function() {
    clearTimeout(this.timeoutId); // clear existing timeout
  }

}
</script>
<style lang="scss">
.data-table-wrapper {
  border-radius: var(--border-radius);
  padding: 20px 20px 20px 20px;
  background-color: var(--panel-background);

  .table-filter-expression-wrapper {
    padding: 0;
    margin: 0;
    min-height: 75px;
    .header-filter-expression {
      margin-bottom: 10px;
    }
  }

  //todo , &.last-documents cell metadata right cli
  &.buildings, &.spaces, &.networks, &.meetings {
    .vy-table-wrapper .vy-table.tabulator .tabulator-row {
      cursor: pointer;
      &.tabulator-selected,  &:hover {
        background-color: var(--active-background);
      }
    }
  }

  .cell-full-width {
    width: 100%;
  }
  
}
</style>
<style lang="scss">
</style>